import { MouseEvent, useCallback, useMemo, useState } from "react"
import {
  Box,
  Button,
  Fade,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  useTheme,
} from "@mui/material"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import clsx from "clsx"
import { isFunction, uniq } from "lodash"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { getFeatureDetail } from "utils/feature"
import { getIcon } from "utils/icon"
import { CloseIcon } from "components/icons"
import { Image } from "components/Image"
import { useScrollToCenter } from "hooks/scrollbar"
import { useInView } from "react-intersection-observer"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const KioskOrdinalSelector = ({
  selectedOrdinal,
  levels,
  onChangeOrdinal,
  currentVenue,
  viewingVenueId,
  locationOrdinal,
}) => {
  const [rootView, setRootView] = useState<Element | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const popoverElementId = open ? "floor-selector-popover" : undefined

  const { ref: userLocationRef, inView } = useInView({
    root: rootView || null,
    threshold: 0,
  })

  const {
    scrollContainerRef,
    displayScroll,
    addItemRef,
    handleContainerScroll,
    scrollIntoItem,
    handleStartScrollClick,
    handleEndScrollClick,
  } = useScrollToCenter({ vertical: true })

  const theme = useTheme()
  const {
    root,
    openButton,
    floorAvatar,
    openIconWrapper,
    closeIconWrapper,
    contentContainer,
    logoImage,
    logoWrapper,
    levelContainer,
    levelList,
    levelButton,
    currentLocationPopper,
    currentLocation,
    logoContainer,
    popoverRoot,
    scrollUpButton,
    scrollDownButton,
  } = theme.components[`OrdinalSelector`]?.styleOverrides || {}

  const { popoverProps = {}, popperProps = {} } =
    theme.components[`OrdinalSelector`]?.defaultProps || {}
  const { t } = useTranslation()
  const { venues = [] } = useVenue()

  const arrowUpIcon = useMemo(
    () => getIcon(theme?.assets?.arrowUpIcon) || <KeyboardArrowUpIcon />,
    [theme]
  )
  const arrowDownIcon = useMemo(
    () => getIcon(theme?.assets?.arrowDownIcon) || <KeyboardArrowUpIcon />,
    [theme]
  )

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.ordinalSelector?.closeIcon) || <CloseIcon />,
    [theme]
  )

  // === Prepare floor data for rendering ===
  const currentLevel = useMemo(
    () =>
      levels.find(({ properties }) => selectedOrdinal === properties.ordinal),
    [levels, selectedOrdinal]
  )

  const venueInfos = useMemo(() => venues.map(getFeatureDetail), [venues])

  const ordinalsByVenue = useMemo(() => {
    const groupedOrdinalByVenue = {}
    venueInfos.forEach((venue) => {
      const venueLevels = levels.filter(
        (level) =>
          level.properties.venue.properties.reference === venue.reference
      )

      groupedOrdinalByVenue[venue.reference] = {}

      venueLevels.forEach((level) => {
        const ordinal = level.properties?.ordinal
        groupedOrdinalByVenue[venue.reference][ordinal] = level
      })
    })
    return groupedOrdinalByVenue
  }, [venueInfos, levels])

  const allOrdinals = useMemo(
    () => uniq<number>(levels.map((level) => level.properties?.ordinal)),
    [levels]
  )

  // === Handle popover interactions ===
  const togglePopupover = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl((prev) => (!prev ? event.currentTarget : null))
    },
    []
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleEnteringPopupover = useCallback(() => {
    const refElementKey = `${viewingVenueId}-${selectedOrdinal}`
    scrollIntoItem(refElementKey)
  }, [scrollIntoItem, viewingVenueId, selectedOrdinal])

  const handleClick = useCallback(
    (data: { ordinal: number; venueId: string }) => () => {
      const { ordinal, venueId } = data || {}

      if (!isFunction(onChangeOrdinal)) return

      const refElementKey = `${venueId}-${ordinal}`
      scrollIntoItem(refElementKey)
      onChangeOrdinal(data)
    },
    [scrollIntoItem, onChangeOrdinal]
  )

  return (
    <>
      <Box
        className={open ? "is-open" : ""}
        sx={root}
        aria-describedby={popoverElementId}
      >
        <Button
          className={open ? "is-open" : ""}
          onClick={togglePopupover}
          sx={openButton}
        >
          <Box sx={openIconWrapper} className="OrdinalSelector-openIconWrapper">
            {arrowUpIcon}
          </Box>
          <Box sx={floorAvatar} className="OrdinalSelector-floorAvatar">
            {currentLevel?.properties?.name?.en}
          </Box>
          <Box
            sx={closeIconWrapper}
            className="OrdinalSelector-closeIconWrapper"
          >
            {closeIcon}
          </Box>
        </Button>
      </Box>
      <Popover
        TransitionProps={{
          onEntering: handleEnteringPopupover,
        }}
        {...popoverProps}
        id={popoverElementId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={popoverRoot}
      >
        <LeftToRightWrapper>
          <Box sx={contentContainer}>
            <Box sx={logoContainer}>
              {venueInfos.map(({ id, name, logo, reference }) => {
                return (
                  <Box sx={logoWrapper} key={id} className={reference}>
                    {logo ? <Image src={logo} sx={logoImage} /> : t(name)}
                  </Box>
                )
              })}
            </Box>
            <Fade in={displayScroll.start}>
              <IconButton sx={scrollUpButton} onClick={handleStartScrollClick}>
                {arrowUpIcon}
              </IconButton>
            </Fade>
            <Box
              sx={levelContainer}
              ref={(el) => {
                setRootView(el as Element)
                scrollContainerRef.current = el
              }}
              onScroll={handleContainerScroll}
            >
              {venueInfos.map(({ reference, id: venueId }) => {
                const levelsInVenue = ordinalsByVenue[reference]
                return (
                  <List
                    className={reference}
                    key={`ordinal-selector-${reference}`}
                    sx={levelList}
                  >
                    {allOrdinals.map((ordinal) => {
                      const level = levelsInVenue[ordinal]
                      const isLevelUnavailable = !level
                      const isCurrentLocation =
                        currentVenue === venueId && locationOrdinal === ordinal
                      const showUserLocationMark =
                        inView && open && isCurrentLocation
                      const isActiveOrdinal =
                        viewingVenueId === venueId &&
                        selectedOrdinal === ordinal
                      const key = `${reference}-ordinal-${ordinal}`
                      const refElementKey = `${venueId}-${ordinal}`
                      return (
                        <Tooltip
                          key={key}
                          ref={isCurrentLocation ? userLocationRef : null}
                          open={showUserLocationMark}
                          placement="right"
                          title={<Box sx={currentLocation} />}
                          leaveDelay={0}
                          slotProps={{
                            popper: {
                              ...popperProps,
                              sx: currentLocationPopper,
                            },
                          }}
                        >
                          <ListItemButton
                            sx={levelButton}
                            ref={(el) => addItemRef(refElementKey, el)}
                            className={clsx(
                              reference,
                              `ordinal-${ordinal}`,
                              isLevelUnavailable ? "is-unavailable" : ""
                            )}
                            // Do not bind click handler to unavailable floor buttons
                            onClick={
                              isLevelUnavailable
                                ? null
                                : handleClick({ ordinal, venueId })
                            }
                            // Do not pass isActiveOrdinal to unavailable floor buttons
                            selected={isActiveOrdinal}
                          >
                            {level?.properties?.name?.en}
                          </ListItemButton>
                        </Tooltip>
                      )
                    })}
                  </List>
                )
              })}
            </Box>
            <Fade in={displayScroll.end}>
              <IconButton sx={scrollDownButton} onClick={handleEndScrollClick}>
                {arrowDownIcon}
              </IconButton>
            </Fade>
          </Box>
        </LeftToRightWrapper>
      </Popover>
    </>
  )
}
